import { useEffect, useMemo, useState } from 'preact/hooks';
const STORAGE_PREFIX = 'pipeline-dismissed';
const CELEBRATION_DWELL_MS = 5000;
function safeGet(key) {
    try {
        return window.localStorage.getItem(key);
    }
    catch {
        return null;
    }
}
function safeSet(key, value) {
    try {
        window.localStorage.setItem(key, value);
    }
    catch { /* localStorage unavailable */ }
}
function safeRemove(key) {
    try {
        window.localStorage.removeItem(key);
    }
    catch { /* localStorage unavailable */ }
}
/**
 * Drives the "setup pipeline" visibility for the assets tab.
 *
 * Behaviour:
 *  - While not all steps complete: pipeline visible.
 *  - When the third step finishes: pipeline stays visible for {@link CELEBRATION_DWELL_MS}
 *    so the user gets visual confirmation, then a per-Web flag is written to
 *    localStorage and the pipeline hides.
 *  - On a subsequent visit to the same Web with the flag already set: pipeline hidden.
 *  - If the actual state ever regresses below 3/3 (e.g. user deletes an AI asset): the
 *    stale flag is cleared and the pipeline reappears.
 *
 * The flag is intentionally local to the device — dismissal is a per-user UI preference,
 * not a property of the Web. Two users opening the same Web shouldn't share dismissal state.
 */
export function useSetupPipelineVisibility({ clientCompanyId, workspaceId, campaignId, webId, allStepsComplete, }) {
    const storageKey = useMemo(() => `${STORAGE_PREFIX}:${clientCompanyId}:${workspaceId}:${campaignId}:${webId}`, [clientCompanyId, workspaceId, campaignId, webId]);
    // Seed initial visibility from storage so we don't flash the pipeline on remount of an
    // already-dismissed Web. If state isn't currently complete we ignore any saved flag —
    // the regression effect below will clear it.
    const [hidden, setHidden] = useState(() => allStepsComplete && safeGet(storageKey) !== null);
    // Re-seed `hidden` whenever the storageKey or completion state changes (e.g. user
    // navigates between Webs). Without this, `hidden=true` from a previously dismissed
    // Web would persist into a fresh Web that has no flag and isn't complete.
    // Also handles the regression case: if a saved flag exists but steps are no longer
    // complete, drop the flag so the pipeline reappears.
    useEffect(() => {
        if (!allStepsComplete) {
            if (safeGet(storageKey) !== null)
                safeRemove(storageKey);
            setHidden(false);
            return;
        }
        setHidden(safeGet(storageKey) !== null);
    }, [allStepsComplete, storageKey]);
    // Celebration dwell + dismiss. When all three steps complete and we don't already have a
    // saved flag, leave the pipeline visible briefly so the user sees 3/3, then persist and
    // hide. Already-dismissed Webs short-circuit the timer so the pipeline stays hidden.
    useEffect(() => {
        if (!allStepsComplete)
            return;
        if (safeGet(storageKey) !== null) {
            setHidden(true);
            return;
        }
        // Make sure the celebration is visible while the timer runs, even if a previous
        // regression had set hidden=false earlier in this session.
        setHidden(false);
        const timer = setTimeout(() => {
            safeSet(storageKey, '1');
            setHidden(true);
        }, CELEBRATION_DWELL_MS);
        return () => clearTimeout(timer);
    }, [allStepsComplete, storageKey]);
    return { hidden };
}
